import React, { RefObject, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton } from "@refinedev/mui";
import { useNotification, useTranslate } from "@refinedev/core";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";

import { TableCell, ModalRef } from "@shared/components";
import { CopyTextButton } from "@modules/erpIntegrations/components/CopyTextButton";
import { patchIntegrationInstructions } from "@modules/erpIntegrations/services/mutations";
import { listIntegrationsQueryKey } from "@modules/erpIntegrations/services/queries";
import { getErpErrorMessage } from "@modules/erpIntegrations/utils";

function InstructionsCell({
  id,
  text,
  title,
  onSaved,
}: {
  id: number;
  text: string;
  title: string;
  onSaved: () => void;
}) {
  const translate = useTranslate();
  const { open: notify } = useNotification();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text ?? "");
  const value = text ?? "";

  const save = useMutation({
    mutationFn: (instructions: string) => patchIntegrationInstructions(Number(id), instructions),
    onSuccess: () => {
      notify?.({ type: "success", message: translate("v1/integration_admin.success.edit") });
      queryClient.invalidateQueries({ queryKey: listIntegrationsQueryKey, refetchType: "all" });
      onSaved();
      setOpen(false);
    },
    onError: (error) => {
      notify?.({ type: "error", message: getErpErrorMessage(error) });
    },
  });

  const handleOpen = () => {
    setDraft(value);
    setOpen(true);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "100%", minWidth: 0 }}>
      <Link
        component="button"
        type="button"
        underline="hover"
        noWrap
        onClick={(event) => {
          event.stopPropagation();
          handleOpen();
        }}
        sx={{ display: "block", textAlign: "left", flex: 1, minWidth: 0 }}
      >
        {value.trim() ? value : "—"}
      </Link>
      <Box onClick={(event) => event.stopPropagation()}>
        <CopyTextButton text={value} />
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {title}
          <CopyTextButton text={draft} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 260, overflowY: "auto", pt: 1 }}>
            <TextField
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setOpen(false)}>
            {translate("buttons.cancel")}
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {translate("buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export const useCatalogColumns = (
  translate: (key: string) => string,
  editModalRef: RefObject<ModalRef>,
  onInstructionsSaved: () => void
): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("v1/integration_admin.fields.id"),
        width: 80,
      },
      {
        field: "name",
        headerName: translate("v1/integration_admin.fields.name"),
        minWidth: 180,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text value={value} textAlign="left" />
        ),
      },
      {
        field: "description",
        headerName: translate("v1/integration_admin.fields.description"),
        minWidth: 220,
        flex: 1,
        sortable: false,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text value={value} textAlign="left" />
        ),
      },
      {
        field: "documentation_url",
        headerName: translate("v1/integration_admin.fields.documentation_url_column"),
        description: translate("v1/integration_admin.fields.documentation_url"),
        width: 160,
        sortable: false,
        headerAlign: "left",
        renderCell: ({ value }) =>
          value ? (
            <Tooltip
              title={value}
              placement="right"
              slotProps={{
                tooltip: { sx: { maxWidth: "none" } },
                popper: {
                  modifiers: [
                    { name: "flip", enabled: false },
                    { name: "preventOverflow", enabled: false },
                  ],
                },
              }}
            >
              <Link
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                noWrap
                sx={{ display: "block" }}
              >
                {value}
              </Link>
            </Tooltip>
          ) : (
            <TableCell.Text value="—" textAlign="left" />
          ),
      },
      {
        field: "instructions",
        headerName: translate("v1/integration_admin.fields.instructions"),
        minWidth: 220,
        flex: 1,
        sortable: false,
        headerAlign: "left",
        renderCell: ({ row, value }) => (
          <InstructionsCell
            id={row.id}
            text={value ?? ""}
            title={translate("v1/integration_admin.fields.instructions")}
            onSaved={onInstructionsSaved}
          />
        ),
      },
      {
        field: "active",
        headerName: translate("v1/integration_admin.fields.active"),
        minWidth: 100,
        sortable: false,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text value={value ? "Sim" : "Não"} textAlign="center" />
        ),
      },
      {
        field: "agreements_count",
        headerName: translate("v1/integration_admin.fields.agreements_count"),
        description: translate("v1/integration_admin.fields.agreements_count_hint"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text value={value ?? 0} textAlign="center" />
        ),
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 220,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <EditButton
              hideText
              onClick={() => editModalRef.current?.open(row)}
            />
          </TableCell.Container>
        ),
      },
    ],
    [translate, editModalRef, onInstructionsSaved]
  );
};
